import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';

import { Subscription } from 'rxjs';
import { ListCalendarEventsGQL, ListCalendarEventsQuery } from '../../../../generated/graphql.generated';
import { dayjs } from '../../../core/dayjs/dayjs';
import { selectJobId } from '../../job-tool.reducer';

/** Minimal shape of an ops-task event shown on the job page. */
export interface JobOpsTask {
  id: string;
  title: string;
  start?: number | null;
  end?: number | null;
  notes?: string;
  clientName?: string;
  clientPhone?: string;
  clientEmail?: string;
}

function parseMetadata(metadata: any): Record<string, any> {
  if (!metadata) return {};
  if (typeof metadata === 'string') {
    try { return JSON.parse(metadata); } catch { return {}; }
  }
  return metadata;
}

/** Plain text of a serialized Quill delta (or raw string fallback). */
function quillToPlainText(notes: unknown): string {
  if (typeof notes !== 'string' || !notes) return '';
  try {
    const delta = JSON.parse(notes);
    return (delta?.ops || [])
      .filter((op: any) => typeof op.insert === 'string')
      .map((op: any) => op.insert)
      .join('')
      .trim();
  } catch {
    return notes;
  }
}

@Component({
  selector: 'app-overview-ops-tasks',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section *ngIf="tasks.length" class="ops-tasks-display p-mt-3">
      <header class="ops-tasks-display__header">
        <i class="pi pi-list"></i>
        <span>Ops Tasks</span>
      </header>
      <ul class="ops-tasks-display__list">
        <li *ngFor="let task of tasks" class="ops-tasks-display__task">
          <div class="ops-task-row">
            <span class="ops-task-title">{{ task.title }}</span>
            <span class="ops-task-time" *ngIf="task.time">{{ task.time }}</span>
          </div>
          <div *ngIf="task.notes" class="ops-task-notes">{{ task.notes }}</div>
          <div class="ops-task-client" *ngIf="task.clientName || task.clientPhone || task.clientEmail">
            <span *ngIf="task.clientName">{{ task.clientName }}</span>
            <a *ngIf="task.clientPhone" [href]="'tel:' + task.clientPhone">{{ task.clientPhone }}</a>
            <a *ngIf="task.clientEmail" [href]="'mailto:' + task.clientEmail">{{ task.clientEmail }}</a>
          </div>
        </li>
      </ul>
    </section>
  `,
  styles: [`
    :host { display: block; }
    .ops-tasks-display {
      background-color: var(--chip-bg);
      border-radius: 8px;
      padding: 1rem;
      margin: 0;
      text-align: left;
    }
    .ops-tasks-display__header {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 0.4rem;
      font-size: 0.857rem;
      font-weight: 600;
      color: var(--text-color-secondary);
      margin-bottom: 0.5rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      i { font-size: 0.857rem; }
    }
    .ops-tasks-display__list {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    .ops-tasks-display__task {
      padding: 0.5rem 0;
      border-bottom: 1px solid var(--divider-color, rgba(0,0,0,0.06));
      &:last-child { border-bottom: none; }
    }
    .ops-task-row {
      display: flex;
      align-items: baseline;
      gap: 0.75rem;
      flex-wrap: wrap;
    }
    .ops-task-title { font-weight: 600; font-size: 0.929rem; }
    .ops-task-time { font-size: 0.786rem; color: var(--text-color-secondary); }
    .ops-task-notes {
      margin-top: 0.25rem;
      font-size: 0.857rem;
      color: var(--text-color);
      white-space: pre-wrap;
      word-break: break-word;
    }
    .ops-task-client {
      margin-top: 0.25rem;
      font-size: 0.786rem;
      color: var(--text-color-secondary);
      display: flex;
      gap: 0.75rem;
      flex-wrap: wrap;
      a { color: var(--text-color-secondary); }
    }
  `],
})
export class OverviewOpsTasksComponent implements OnInit, OnDestroy {

  public tasks: JobOpsTask[] = [];

  private subs = new Subscription();
  private loadedForJobId: string | null = null;

  constructor(
    private store: Store,
    private listCalendarEventsGQL: ListCalendarEventsGQL,
  ) { }

  ngOnInit(): void {
    this.subs.add(
      this.store.select(selectJobId).subscribe((jobId) => {
        if (jobId && jobId !== this.loadedForJobId) {
          this.loadedForJobId = jobId;
          this.loadTasks(jobId);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadTasks(jobId: string): void {
    this.tasks = [];
    this.subs.add(
      this.listCalendarEventsGQL.watch({
        filter: {
          getEventsOfType: ['ops-task'],
        },
        limit: 500,
      } as any)
        .valueChanges
        .subscribe({
          next: (res) => {
            // Ignore stale responses after a route change
            if (this.loadedForJobId !== jobId) return;
            const events = (res?.data?.calendarEvents?.events || []) as ListCalendarEventsQuery['calendarEvents']['events'];
            this.tasks = this.filterTasksForJob(events || [], jobId);
          },
          error: () => {
            this.tasks = [];
          },
        }),
    );
  }

  private filterTasksForJob(events: any[], jobId: string): JobOpsTask[] {
    const matching = events.filter((event) => {
      const meta = parseMetadata(event?.metadata);
      return meta.jobId === jobId;
    });

    return matching
      .map((event) => {
        const meta = parseMetadata(event.metadata);
        const time = this.formatTime(meta, event);
        return {
          id: event.id,
          title: event.title,
          start: event.start,
          end: event.end,
          time,
          notes: quillToPlainText(meta.dayNotes ?? event.description),
          clientName: meta.clientName || undefined,
          clientPhone: meta.clientPhone || undefined,
          clientEmail: meta.clientEmail || undefined,
        };
      })
      .sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
  }

  private formatTime(meta: Record<string, any>, event: any): string {
    const start = typeof event.start === 'number' ? event.start : null;
    const end = typeof event.end === 'number' ? event.end : null;
    if (!start) return '';
    const fmt = 'h:mm A';
    if (!end) return dayjs.unix(start).format(fmt);
    return `${dayjs.unix(start).format(fmt)} - ${dayjs.unix(end).format(fmt)}`;
  }
}